import { z } from 'zod';
import { metricsTypeEnum } from '@features/ai-insights';

const channelGroupingSchema = z.object({
  total: z.number().nullable(),
  inStore: z.number().nullable(),
  online: z.number().nullable(),
});

const salesMetricsSchema = z.object({
  incrementalSalesLift: channelGroupingSchema,
  confidenceLevel: channelGroupingSchema,
  incrementalSales: channelGroupingSchema,
  transactionSizeSales: channelGroupingSchema,
  transactionRateSales: channelGroupingSchema,
  penetrationSales: channelGroupingSchema,
  totalProjectedSales: channelGroupingSchema,
  exposedSalesRate: channelGroupingSchema,
  controlSalesRate: channelGroupingSchema,
  exposedTransactionSize: channelGroupingSchema,
  controlTransactionSize: channelGroupingSchema,
  transactionSizeLift: channelGroupingSchema,
  exposedTransactionRate: channelGroupingSchema,
  controlTransactionRate: channelGroupingSchema,
  transactionRateLift: channelGroupingSchema,
  exposedPenetration: channelGroupingSchema,
  controlPenetration: channelGroupingSchema,
  penetrationLift: channelGroupingSchema,
  incrementalSalesLiftPercentageIndex: channelGroupingSchema,
  totalSalesPerImpressionIndex: channelGroupingSchema,
  incrementalSalesPerImpressionIndex: channelGroupingSchema,
});

const footTrafficAIInsightSchema = z.object({
  dimensionName: z.string().nullable(),
  publisher: z.string().nullable(),
  summary: z.array(z.string()),
  metricsType: metricsTypeEnum,
});

const rsaAIInsightSchema = z.object({
  metricsType: metricsTypeEnum,
  dimensionName: z.string().nullable(),
  summary: z.array(z.string()),
});

export const lci3ReportSchema = z.object({
  id: z.number(),
  salesReportId: z.number().nullable(),
  salesReportStatus: z.enum(['pending', 'approved']).nullable(),
  campaignId: z.number(),
  startDate: z.string(),
  reportDate: z.string(),
  name: z.string(),
  version: z.string(),
  reportType: z.enum(['weekly', 'custom', 'partner']),
  status: z.enum(['pending', 'approved']),
  viewInDashboard: z.boolean(),
  reportGroup: z.string(),
  salesLiftEnable: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
  statisticallySignificant: z.boolean(),
  footTrafficAIInsights: z.array(footTrafficAIInsightSchema),
  rsaAIInsights: z.array(rsaAIInsightSchema).nullable(),
  visitMetrics: z.object({
    impressions: z.number(),
    uniqueUsers: z.number(),
    locationsMeasured: z.number(),
    incrementalVisits: z.number().nullable(),
    incrementalLift: z.number().nullable(),
    confidenceLevel: z.number().nullable(),
    matchedForAttribution: z.number().nullable(),
    projectedVisits: z.number().nullable(),
    costPerVisit: z.number().nullable(),
    costPerIncrementalVisit: z.number().nullable(),
    visitCountCumulative: z.number(),
  }),
  salesMetrics: salesMetricsSchema.nullable(),
  subdimensions: z.array(
    z.object({
      reportId: z.number(),
      name: z.string(),
      value: z.string(),
      rawValue: z.string(),
      statisticallySignificant: z.boolean(),
      visitMetrics: z.object({
        optimizationIndex: z.number().nullable(),
        confidenceLevel: z.number().nullable(),
        impressions: z.number(),
        uniqueUsers: z.number(),
        incrementalLift: z.number().nullable(),
        incrementalVisits: z.number().nullable(),
        projectedVisits: z.number().nullable(),
        incrementalVisitLiftPercentageIndex: z.number().nullable(),
        totalVisitsPerImpressionIndex: z.number().nullable(),
        incrementalVisitsPerImpressionIndex: z.number().nullable(),
      }),
      salesMetrics: salesMetricsSchema.nullable(),
    })
  ),
});

export const lci3ReportsSchema = z.array(lci3ReportSchema);

export type Lci3Report = z.infer<typeof lci3ReportsSchema>[number];
export type VisitMetrics = Lci3Report['visitMetrics'];
export type Subdimension = Lci3Report['subdimensions'][number];
export type SubdimensionVisitMetrics = Subdimension['visitMetrics'];
export type Lci3SalesMetrics = z.infer<typeof salesMetricsSchema>;
export type ChannelGrouping = z.infer<typeof channelGroupingSchema>;
export type ChannelGroupingKey = keyof ChannelGrouping;
